import React from "react";
import { useTranslation } from "react-i18next";
import { Table, Empty, Tag, Tooltip, Typography } from "antd";
import { WarningOutlined } from "@ant-design/icons";

import { Card, CardTitle, CardCaption } from "./dashboardStyles";
import { TableWrap } from "./taskStyles";
import { formatRelativeTime } from "./taskHelpers";
import { outcomeLabel, outcomeTone, TONE_COLORS, stepLabel } from "./dashboardHelpers";
import { ERROR_700 } from "../../constant/colors";

const { Text } = Typography;
const PAGE_SIZE = 8;

/**
 * "Needs attention now" — patients whose LATEST run failed (a patient who was
 * re-run or recovered drops off this list). CURRENT-state, so it ignores the
 * dashboard's date range. Each row links to the patient, names the step the
 * run crashed in, the terminal outcome, and the coarse PHI-free error string —
 * enough for a navigator to pick up and act without opening the run first.
 */
const FailedRunsPanel = ({ stats }) => {
  const { t } = useTranslation();

  const rows = stats?.failedNeedingAttention ?? [];
  // Uncapped count — the list itself is capped server-side (100 rows), so under
  // a flood the badge must show the true total, not the truncated length.
  // Falls back to the list length for older payloads without the field.
  const total = stats?.failedNeedingAttentionTotal ?? rows.length;

  const columns = [
    {
      title: t("agentic_dashboard.failed.col_patient"),
      key: "patient",
      width: 130,
      render: (_v, row) => (
        <a className="who-link" href={`/patient/${row.patientId}`} target="_blank" rel="noreferrer">
          {t("agentic_dashboard.failed.patient_label", { id: row.patientId })}
        </a>
      )
    },
    {
      title: t("agentic_dashboard.failed.col_failed_step"),
      key: "failedStep",
      width: 170,
      render: (_v, row) => <span className="muted">{stepLabel(row.failedStep, t)}</span>
    },
    {
      title: t("agentic_dashboard.failed.col_outcome"),
      key: "outcome",
      width: 150,
      render: (_v, row) => {
        const tone = outcomeTone(row.finalOutcomeCode);
        return (
          <Tag color={tone === "error" ? "error" : "warning"} style={{ marginInlineEnd: 0 }}>
            {outcomeLabel(row.finalOutcomeCode, t)}
          </Tag>
        );
      }
    },
    {
      title: t("agentic_dashboard.failed.col_detail"),
      key: "detail",
      render: (_v, row) =>
        row.detail ? (
          <Tooltip title={row.detail}>
            <Text className="muted" ellipsis style={{ maxWidth: 320 }}>
              {row.detail}
            </Text>
          </Tooltip>
        ) : (
          <span className="muted">—</span>
        )
    },
    {
      title: t("agentic_dashboard.failed.col_when"),
      key: "when",
      width: 130,
      render: (_v, row) => <span className="muted">{formatRelativeTime(row.completedAt)}</span>
    }
  ];

  return (
    <Card data-testid="agentic-dashboard-failed">
      <CardTitle>
        <WarningOutlined style={{ color: TONE_COLORS.error.bar }} />
        {t("agentic_dashboard.failed.title")}
        {total > 0 && <span style={{ color: ERROR_700, fontWeight: 600 }}>({total.toLocaleString()})</span>}
      </CardTitle>
      <CardCaption>{t("agentic_dashboard.failed.caption")}</CardCaption>
      {total > rows.length && (
        <CardCaption data-testid="agentic-dashboard-failed-truncated">
          {t("agentic_dashboard.failed.truncated", { shown: rows.length, total: total.toLocaleString() })}
        </CardCaption>
      )}

      {rows.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("agentic_dashboard.failed.empty")} />
      ) : (
        <TableWrap>
          <Table
            size="small"
            rowKey="agenticRunId"
            columns={columns}
            dataSource={rows}
            pagination={rows.length > PAGE_SIZE ? { pageSize: PAGE_SIZE, showSizeChanger: false } : false}
          />
        </TableWrap>
      )}
    </Card>
  );
};

export { FailedRunsPanel };
